const LIST_MARKERS = ['- ', '* ', '+ ', '• '];
const CELL_SEPARATOR = ' · ';
const EMPHASIS = ['**', '__', '`'];

function isSeparatorCell(cell: string): boolean {
  return [...cell].every(character => character === '-' || character === ':');
}

function tableRow(line: string): string {
  const cells = line
    .split('|')
    .map(cell => cell.trim())
    .filter(Boolean);
  if (cells.length === 0 || cells.every(isSeparatorCell)) return '';
  return cells.join(CELL_SEPARATOR);
}

function stripLeadingMarker(line: string): string {
  let text = line;
  while (text.startsWith('#')) text = text.slice(1);
  text = text.trimStart();

  const bullet = LIST_MARKERS.find(marker => text.startsWith(marker));
  if (bullet) return text.slice(bullet.length);

  const dot = text.indexOf('. ');
  const isNumbered =
    dot > 0 &&
    dot <= 3 &&
    [...text.slice(0, dot)].every(character => character >= '0' && character <= '9');
  return isNumbered ? text.slice(dot + 2) : text;
}

/**
 * A source passage as prose. Quotes are lifted verbatim from a document's markdown, so they arrive
 * carrying its decoration — bold markers, a leading bullet, a heading hash, table pipes — which
 * reads as stray punctuation once set as a quotation. Only the rendering is cleaned; the verbatim
 * quote is what the evidence was grounded against.
 *
 * Decoration is removed, never content. A table row keeps every cell it has, including a lone `-`
 * standing for nil; only a row that is entirely rule characters is dropped, being layout. Single
 * `*` and `_` are left alone — they carry arithmetic and identifiers far more often than italics.
 */
export function plainQuote(quote: string): string {
  const text = quote
    .split('\n')
    .map(line => line.trim())
    .filter(Boolean)
    .map(line => (line.startsWith('|') ? tableRow(line) : stripLeadingMarker(line)))
    .filter(Boolean)
    .join(' ');
  return EMPHASIS.reduce((stripped, marker) => stripped.split(marker).join(''), text).trim();
}
